/**
 * Resolution of the outbound `postMessage` targetOrigin for the embeddable
 * booking widget (#458 / #474).
 *
 * The widget iframe posts messages (`rr-widget:ready` / `:resize` / `:error` /
 * `:booking-complete`) up to its embedding parent. `postMessage`'s second
 * argument — the targetOrigin — must be known in advance by the *sender*. A
 * naive implementation that always used `allowedEmbedOrigins[0]` broke whenever
 * an operator configured 2+ origins and embedded on the 2nd+ site: the message
 * was targeted at the first origin and silently dropped by the browser.
 *
 * The correct resolution matches the *real* embedding parent (derived from
 * `document.referrer`) against the operator's allowlist and targets that exact
 * origin when it's allowed — narrowing the target for every configured origin,
 * not just the first. It degrades to `"*"` only when the parent can't be
 * resolved or matched (e.g. `Referrer-Policy: no-referrer`), preserving the
 * open/legacy behaviour for unconfigured operators.
 */

/**
 * The real embedding parent origin, read from `document.referrer`.
 *
 * The browser sets `document.referrer` to the parent document's URL when a
 * cross-origin iframe is loaded, so its origin is the embedding site. Returns
 * `null` when there is no referrer (e.g. `Referrer-Policy: no-referrer`) or it
 * can't be parsed — callers then fall back per {@link resolveTargetOrigin}.
 *
 * @returns The embedding parent's origin, or `null` when unavailable/unparseable.
 */
export function getParentOrigin(): string | null {
  if (typeof document === "undefined" || !document.referrer) return null;
  try {
    return new URL(document.referrer).origin;
  } catch {
    return null;
  }
}

/**
 * Resolve the outbound `postMessage` targetOrigin so it works for EVERY
 * configured origin, not just `origins[0]`.
 *
 * Resolution order:
 *   1. If the real embedding parent origin is known AND present in the
 *      operator's allowlist, target it exactly (the narrowest correct target).
 *   2. Else, if exactly one origin is configured, target it (unambiguous).
 *   3. Else, `"*"` — the parent can't be resolved/matched, or none/many origins
 *      are configured with no referrer to disambiguate (open/legacy behaviour).
 *
 * Non-sensitive messages still use this; the operator is advised (in the
 * EmbedPanel) that a single canonical origin yields the strictest target.
 *
 * @param origins - The operator's configured allowed embed origins (may be empty/unset).
 * @param parentOrigin - The real embedding parent origin (from {@link getParentOrigin}), or null.
 * @returns The narrowed targetOrigin, or `"*"` when it cannot be resolved.
 */
export function resolveTargetOrigin(
  origins: string[] | undefined,
  parentOrigin: string | null
): string {
  if (parentOrigin && origins?.includes(parentOrigin)) {
    return parentOrigin;
  }
  if (origins?.length === 1) {
    return origins[0] ?? "*";
  }
  return "*";
}
